import React, { forwardRef } from 'react';
import { cn } from './tokens';

// =============================================================================
// BUTTON VARIANTS
// =============================================================================

const variants = {
  primary: `
    bg-accent text-black font-bold
    hover:bg-accent/90
    active:bg-accent/80
    disabled:bg-accent/50 disabled:cursor-not-allowed
  `,
  secondary: `
    bg-slate-800 text-slate-50 font-medium
    border border-slate-700
    hover:bg-slate-700 hover:border-slate-600
    active:bg-slate-600
    disabled:bg-slate-800/50 disabled:text-slate-400 disabled:cursor-not-allowed
  `,
  ghost: `
    bg-transparent text-slate-300 font-medium
    hover:bg-slate-800 hover:text-slate-50
    active:bg-slate-700
    disabled:text-slate-400 disabled:cursor-not-allowed
  `,
  danger: `
    bg-red-600 text-white font-bold
    hover:bg-red-500
    active:bg-red-700
    disabled:bg-red-600/50 disabled:cursor-not-allowed
  `,
  success: `
    bg-green-600 text-white font-bold
    hover:bg-green-500
    active:bg-green-700
    disabled:bg-green-600/50 disabled:cursor-not-allowed
  `,
  outline: `
    bg-transparent text-accent font-medium
    border border-accent
    hover:bg-accent/10
    active:bg-accent/20
    disabled:border-accent/50 disabled:text-accent/50 disabled:cursor-not-allowed
  `,
  link: `
    bg-transparent text-accent font-medium
    hover:underline
    active:text-accent/80
    disabled:text-accent/50 disabled:cursor-not-allowed disabled:no-underline
  `,
} as const;

const sizes = {
  xs: 'h-7 px-2 text-xs gap-1',
  sm: 'h-8 px-3 text-sm gap-1.5',
  md: 'h-10 px-4 text-sm gap-2',
  lg: 'h-12 px-6 text-base gap-2',
  xl: 'h-14 px-8 text-lg gap-3',
  icon: 'h-10 w-10 p-0',         // Square icon button
  'icon-sm': 'h-8 w-8 p-0',      // Small icon button
  'icon-xs': 'h-6 w-6 p-0',      // Extra small icon button
} as const;

// =============================================================================
// TYPES
// =============================================================================

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  /** Visual style variant */
  variant?: keyof typeof variants;
  /** Size of the button */
  size?: keyof typeof sizes;
  /** Show loading spinner and disable button */
  isLoading?: boolean;
  /** Loading text to display */
  loadingText?: string;
  /** Icon to display before children */
  leftIcon?: React.ReactNode;
  /** Icon to display after children */
  rightIcon?: React.ReactNode;
  /** Make the button take full width */
  fullWidth?: boolean;
}

// =============================================================================
// COMPONENT
// =============================================================================

/**
 * Button component with consistent styling and multiple variants
 *
 * @example
 * // Primary button
 * <Button variant="primary">Save Changes</Button>
 *
 * @example
 * // Loading state
 * <Button variant="primary" isLoading loadingText="Saving...">Save</Button>
 *
 * @example
 * // With icons
 * <Button variant="secondary" leftIcon={<Plus size={16} />}>Add Item</Button>
 *
 * @example
 * // Icon-only button
 * <Button variant="ghost" size="icon" aria-label="Settings">
 *   <Settings size={20} />
 * </Button>
 */
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant = 'primary',
      size = 'md',
      isLoading = false,
      loadingText,
      leftIcon,
      rightIcon,
      fullWidth = false,
      disabled,
      className,
      children,
      ...props
    },
    ref
  ) => {
    const isDisabled = disabled || isLoading;
    const isIconButton = size.startsWith('icon');

    return (
      <button
        ref={ref}
        disabled={isDisabled}
        className={cn(
          // Base styles
          'inline-flex items-center justify-center',
          'rounded-lg',
          'transition-all duration-200',
          'focus:outline-none focus:ring-2 focus:ring-accent/50 focus:ring-offset-0',
          // Variant styles
          variants[variant],
          // Size styles
          sizes[size],
          // Full width
          fullWidth && 'w-full',
          // Custom classes
          className
        )}
        {...props}
      >
        {/* Loading spinner */}
        {isLoading && (
          <div className={cn(
            'border-current/20 border-t-current rounded-full animate-spin',
            (size === 'xs' || size === 'sm' || size === 'icon-xs') ? 'w-3.5 h-3.5 border-2' : 'w-[18px] h-[18px] border-2'
          )} />
        )}

        {/* Left icon (hidden when loading) */}
        {!isLoading && leftIcon && leftIcon}

        {/* Button text */}
        {!isIconButton && (
          <span>
            {isLoading && loadingText ? loadingText : children}
          </span>
        )}

        {/* Icon-only content (when not loading) */}
        {isIconButton && !isLoading && children}

        {/* Right icon (hidden when loading) */}
        {!isLoading && rightIcon && rightIcon}
      </button>
    );
  }
);

Button.displayName = 'Button';

// =============================================================================
// BUTTON GROUP
// =============================================================================

interface ButtonGroupProps {
  /** Buttons to group */
  children: React.ReactNode;
  /** Orientation of the group */
  orientation?: 'horizontal' | 'vertical';
  /** Additional class names */
  className?: string;
}

/**
 * Group multiple buttons together with connected borders
 *
 * @example
 * <ButtonGroup>
 *   <Button variant="secondary">Left</Button>
 *   <Button variant="secondary">Center</Button>
 *   <Button variant="secondary">Right</Button>
 * </ButtonGroup>
 */
export const ButtonGroup: React.FC<ButtonGroupProps> = ({
  children,
  orientation = 'horizontal',
  className,
}) => {
  return (
    <div
      className={cn(
        'inline-flex',
        orientation === 'horizontal'
          ? '[&>*:not(:first-child)]:rounded-l-none [&>*:not(:last-child)]:rounded-r-none [&>*:not(:first-child)]:border-l-0'
          : 'flex-col [&>*:not(:first-child)]:rounded-t-none [&>*:not(:last-child)]:rounded-b-none [&>*:not(:first-child)]:border-t-0',
        className
      )}
      role="group"
    >
      {children}
    </div>
  );
};

// =============================================================================
// ICON BUTTON HELPER
// =============================================================================

interface IconButtonProps extends Omit<ButtonProps, 'size' | 'leftIcon' | 'rightIcon' | 'loadingText'> {
  /** Accessible label for the button (recommended, or use title) */
  'aria-label'?: string;
  /** Icon size variant */
  size?: 'xs' | 'sm' | 'md';
}

/**
 * Convenience component for icon-only buttons
 *
 * @example
 * <IconButton aria-label="Delete item" variant="danger">
 *   <Trash2 size={18} />
 * </IconButton>
 */
export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(
  ({ size = 'md', ...props }, ref) => {
    const sizeMap = {
      xs: 'icon-xs',
      sm: 'icon-sm',
      md: 'icon',
    } as const;

    return <Button ref={ref} size={sizeMap[size]} {...props} />;
  }
);

IconButton.displayName = 'IconButton';

export default Button;
